import { t } from './textos.js';

/**
 * Regras do Scan Pixel que o tablet decide sozinho (Figma, quadros 8–11, e
 * docs/scan-pixel-integracao.md §8). A pontuação que vale é sempre a do
 * servidor: o que este módulo calcula é só o que a tela mostra.
 */

export const PONTOS_POR_ENIGMA = 10;                       // RN-SP-04
export const BONUS_POR_COLOCACAO = Object.freeze({ 1: 30, 2: 20, 3: 10 });   // RN-SP-11..14

const limpar = (texto) => String(texto ?? '').trim().toUpperCase();

/**
 * RN-SP-08: o campo de código aceita letras, números e o ponto (a resposta "1.3" do catálogo),
 * em maiúsculas, até o tamanho da pista.
 */
/**
 * Posição dos campos de código na linha (RI-SP-03). A quantidade de pistas é livre: quando a soma
 * das larguras não cabe em `larguraMaxima`, todos encolhem na mesma escala, centrados.
 * Pura: recebe as larguras naturais e devolve `{ escala, centros }`.
 */
export function distribuirCampos (larguras, { vao = 16, centro = 640, larguraMaxima = 1180 } = {}) {
    const natural = larguras.reduce((soma, l) => soma + l, 0) + vao * Math.max(0, larguras.length - 1);
    const escala = natural > larguraMaxima ? larguraMaxima / natural : 1;
    let x = centro - (natural * escala) / 2;
    const centros = larguras.map((largura) => {
        const meio = x + (largura * escala) / 2;
        x += (largura + vao) * escala;
        return meio;
    });
    return { escala, centros };
}

export function sanitizarCodigo (valor, tamanho) {
    return String(valor ?? '').replace(/[^0-9a-zA-Z.]/g, '').toUpperCase().slice(0, tamanho);
}

/** Converte o catálogo do backend no formato que as cenas usam. */
export function paraEnigmas (catalogo) {
    // AP8 (RN-AP-035): a instrução só abre sozinha com a chave do admin ligada; só o `true` liga.
    const instrucaoAutomatica = catalogo?.instrucao_automatica === true;
    return [...(catalogo?.enigmas ?? [])]
        .sort((a, b) => a.ordem - b.ordem)
        .map((enigma) => ({
            id: enigma.id,
            ordem: enigma.ordem,
            abertura: enigma.texto_charada ?? '',
            material: enigma.material ?? null,
            instrucaoAutomatica,
            // AP12: só o enigma com sorteio gravado traz `ordem_exibicao`; nele a pista diz o campo.
            embaralhado: enigma.pistas.some((pista) => pista.ordem_exibicao != null),
            pistas: [...enigma.pistas].sort((a, b) => a.ordem - b.ordem).map((pista) => ({
                id: pista.id,
                ordem: pista.ordem,
                // AP12: ordem de exibição sorteada — só as abas embaralham; os campos ficam na canônica.
                ordemExibicao: pista.ordem_exibicao ?? pista.ordem,
                enunciado: pista.enunciado ?? '',
                tamanho: pista.tamanho_campo,
                resposta: pista.valor_esperado
            }))
        }));
}

/**
 * AP8 (RN-AP-035): ao entrar no enigma, a instrução abre sozinha só com a chave do admin
 * ligada, com texto de abertura e se ainda não foi vista ali (DP-VAR-030). O "i" não passa
 * por aqui: ele abre a instrução sempre que o jogador toca.
 */
export function deveAbrirInstrucao (enigma, jaVista) {
    return Boolean(enigma?.instrucaoAutomatica && enigma?.abertura) && !jaVista;
}

/**
 * AP10 (RN-AP-043): o que fazer quando o envio do resultado falha. Só o líder sincroniza o
 * grupo: a recusa `apenas_lider` é esperar a colocação dele (chega por evento), não erro.
 * @returns {'aguardar_lider'|'ja_sincronizado'|'pendente'}
 */
export function desfechoDoEnvio (falha) {
    if (falha?.codigo === 'apenas_lider') return 'aguardar_lider';
    if (falha?.codigo === 'fase_ja_sincronizada') return 'ja_sincronizado';
    return 'pendente';
}

/**
 * AP11 (RN-AP-044): o aviso final do Scan Pixel não tem botão. Quem tira o jogador dali é o
 * facilitador: finalizar pelo painel (`ligarEstacaoDoGrupo`) ou iniciar outra estação do grupo.
 */
export function avisoFinalDoScanPixel () {
    return {
        titulo: t('scan.titulo'),
        largura: 800,
        blocos: [
            { texto: t('scan.fim_titulo'), peso: '600', maiusculas: true },
            { texto: t('scan.fim_texto_1') },
            { texto: t('scan.fim_texto_2') },
            { texto: t('scan.fim_texto_3') }
        ]
    };
}

/**
 * AP11 (RN-AP-045): no resultado do Scan Pixel, outra estação do grupo iniciada ou retomada leva
 * ao Aguarde, que entra nela. Mudanças do próprio Scan Pixel ficam com `ligarEstacaoDoGrupo`.
 */
export function saiDoResultadoScanPixel ({ fase, mudanca } = {}) {
    return fase !== 'scanpixel' && (mudanca === 'iniciada' || mudanca === 'retomada');
}

/** RI-SP-04: Conferir só habilita com todos os campos no tamanho exigido. */
export function podeConferir (pistas, valores) {
    return pistas.length > 0
        && pistas.every((pista, i) => limpar(valores[i]).length === pista.tamanho);
}

/**
 * RN-SP-03 / RN-SP-05 / RN-SP-08: confere cada pista (sem diferenciar caixa);
 * o enigma só está concluído com todas certas.
 */
export function conferir (pistas, valores) {
    const pistasConferidas = pistas.map((pista, i) => ({
        pistaId: pista.id,
        correta: limpar(valores[i]) === limpar(pista.resposta)
    }));
    return { pistas: pistasConferidas, concluido: pistasConferidas.every((p) => p.correta) };
}

/**
 * Regras que a tela usa para mostrar os pontos: as do servidor (editáveis pelo
 * painel), ou os padrões quando elas não vieram. Valor que não é inteiro ≥ 0 cai no padrão.
 */
export function regrasDoScanPixel (regras) {
    const inteiro = (valor, padrao) => (Number.isInteger(valor) && valor >= 0 ? valor : padrao);
    return {
        porEnigma: inteiro(regras?.pontos_por_enigma, PONTOS_POR_ENIGMA),
        bonus: Object.fromEntries([1, 2, 3].map((posicao) => [
            posicao, inteiro(regras?.[`bonus_colocacao_${posicao}`], BONUS_POR_COLOCACAO[posicao])
        ]))
    };
}

/**
 * Linhas da tela de pontuação (quadro 35). `colocacao: undefined` = servidor ainda não respondeu.
 * `regras` são as de `GET /pontuacao/regras`; a pontuação que vale é sempre a do servidor.
 */
export function montarPlacar ({ totalEnigmas, concluidos, colocacao, regras = null }) {
    const { porEnigma, bonus: bonusPorColocacao } = regrasDoScanPixel(regras);
    const linhas = [];
    for (let ordem = 1; ordem <= totalEnigmas; ordem++) {
        linhas.push({ rotulo: t('scan.placar_enigma', { ordem }), pontos: concluidos.includes(ordem) ? porEnigma : 0 });
    }

    const completou = concluidos.length === totalEnigmas;
    const pendente = completou && colocacao === undefined;
    const bonus = completou ? (bonusPorColocacao[colocacao] ?? 0) : 0;
    linhas.push({
        rotulo: bonus > 0 ? t('scan.placar_bonus_colocacao', { colocacao }) : t('geral.bonus'),
        pontos: pendente ? null : bonus
    });

    return { linhas, total: linhas.reduce((soma, linha) => soma + (linha.pontos ?? 0), 0), pendente };
}

const CHAVE = (participanteId) => `fiat-arcade:scanpixel:v1:${participanteId}`;

const novoEstado = () => ({
    inicio: null,            // ISO do primeiro acesso
    fim: null,               // ISO da conclusão do último enigma (RN-SP-10)
    concluidos: [],          // ordens dos enigmas já resolvidos
    instrucoesVistas: [],    // ordens dos enigmas cuja instrução de entrada já foi mostrada
    historico: [],           // todas as tentativas, certas e erradas
    // AP9: só no tablet, nunca no envio. Último código conferido e o que está digitado, por pista.
    ultimosCodigos: {},
    rascunhos: {},
    loteSeq: 0,              // avança a cada "Conferir"
    chaveEnvio: null,        // Idempotency-Key do sync final
    enviado: false,
    colocacao: undefined
});

/**
 * Progresso do participante na fase (RN-SP-02), salvo no tablet para
 * sobreviver a um recarregamento. A fase é offline-first: nada aqui depende
 * de rede até o envio final.
 */
export class ProgressoScanPixel {
    constructor (participanteId, { armazenamento = globalThis.localStorage, agora = () => new Date() } = {}) {
        this._chave = CHAVE(participanteId);
        this._armazenamento = armazenamento;
        this._agora = agora;
        this.estado = novoEstado();
        try {
            const bruto = armazenamento?.getItem(this._chave);
            if (bruto) this.estado = { ...novoEstado(), ...JSON.parse(bruto) };
        } catch { /* estado novo */ }
    }

    /** Índice (0-based) do enigma a jogar: ordem estritamente linear 1→5 (RN-SP-01). */
    get indiceAtual () { return this.estado.concluidos.length; }

    terminou (totalEnigmas) { return this.estado.concluidos.length >= totalEnigmas; }

    /** RI-SP-01: a instrução abre sozinha uma vez por enigma; voltar de Materiais ou do Mural não a repete. */
    instrucaoVista (ordem) { return this.estado.instrucoesVistas.includes(ordem); }

    marcarInstrucaoVista (ordem) {
        if (this.instrucaoVista(ordem)) return;
        this.estado.instrucoesVistas.push(ordem);
        this._gravar();
    }

    iniciar () {
        if (!this.estado.inicio) {
            this.estado.inicio = this._agora().toISOString();
            this._gravar();
        }
    }

    /**
     * Registra um "Conferir": todas as pistas do lote compartilham o mesmo `submissao_lote_seq`.
     * `valores` (os códigos conferidos) ficam só no tablet, para a cena restaurar os campos (AP9).
     */
    registrarSubmissao (enigma, resultado, eUltimo, valores = []) {
        const instante = this._agora().toISOString();
        resultado.pistas.forEach(({ pistaId, correta }, i) => {
            const codigo = String(valores[i] ?? '');
            this.estado.ultimosCodigos[pistaId] = codigo;
            this.estado.rascunhos[pistaId] = codigo;
            this.estado.historico.push({
                enigma_id: enigma.id,
                enigma_ordem: enigma.ordem,
                pista_id: pistaId,
                correta,
                timestamp_tablet: instante,
                submissao_lote_seq: this.estado.loteSeq
            });
        });
        this.estado.loteSeq += 1;

        if (resultado.concluido && !this.estado.concluidos.includes(enigma.ordem)) {
            this.estado.concluidos.push(enigma.ordem);
            if (eUltimo) this.estado.fim = instante;
        }
        this._gravar();
    }

    /** AP9 (RN-AP-036): o que está digitado em cada campo do enigma, gravado a cada tecla. */
    guardarRascunho (enigma, valores) {
        enigma.pistas.forEach((pista, i) => { this.estado.rascunhos[pista.id] = String(valores[i] ?? ''); });
        this._gravar();
    }

    /**
     * AP9 (RN-AP-037): como cada campo do enigma volta quando a cena é recriada (voltar de
     * Materiais, do Aguarde, recarregar). Certa uma vez, volta certa; senão vale o que está
     * digitado, em vermelho só se for o último código conferido errado, sem edição depois.
     */
    estadoDasPistas (enigma) {
        const { historico, ultimosCodigos, rascunhos } = this.estado;
        return enigma.pistas.map((pista) => {
            const certa = historico.some((item) => item.enigma_id === enigma.id && item.pista_id === pista.id && item.correta);
            if (certa) return { valor: pista.resposta, estado: 'ok' };
            const valor = rascunhos[pista.id] ?? ultimosCodigos[pista.id] ?? '';
            return { valor, estado: valor !== '' && valor === ultimosCodigos[pista.id] ? 'erro' : null };
        });
    }

    /** Corpo de `POST /scanpixel/resultado` e a chave de idempotência (a mesma em todo reenvio). */
    envio (gerarChave = () => globalThis.crypto.randomUUID()) {
        if (!this.estado.chaveEnvio) {
            this.estado.chaveEnvio = gerarChave();
            this._gravar();
        }
        return {
            chave: this.estado.chaveEnvio,
            corpo: {
                inicio_tablet: this.estado.inicio,
                fim_tablet: this.estado.fim,
                historico: this.estado.historico
            }
        };
    }

    marcarEnviado (colocacao) {
        this.estado.enviado = true;
        if (colocacao !== undefined) this.estado.colocacao = colocacao;
        this._gravar();
    }

    reiniciar () {
        this.estado = novoEstado();
        this._gravar();
    }

    _gravar () {
        try { this._armazenamento?.setItem(this._chave, JSON.stringify(this.estado)); } catch { /* sem storage */ }
    }
}
